/**
 * @packageDocumentation
 * @module hooks/useParentSize
 */
import { useState, useCallback } from 'react';
import { UseParentSizeProps, UseParentSizeResult } from './types';
import { useResizeObserverRef } from '../useResizeObserver';

/**
 * `useParentSize` will provide width and height of the parent element containing the referenced element.
 * A `ResizeObserver` is used to watch for resize events and update the provided dimensions.
 *
 * @returns A tuple with a `callback ref` and the current `Dimensions` value.
 *
 * @example
 * ```jsx
 * function MyC() {
 *   const [ref, size] = useParentSize();
 *   return <div ref={ref}>
 *    { size && <Table {...size} />}
 *   </div>;
 * }
 * ```
 */
export function useParentSize(props: UseParentSizeProps = {}): UseParentSizeResult {
  const [dimensions, setDimensions] = useState(props.initialDimensions);

  const [setParentRef] = useResizeObserverRef((entry) => {
    const { ignoreHeight, ignoreWidth } = props;
    const parentNode = entry.target;

    if (parentNode) {
      let height = (entry.target as HTMLElement).offsetHeight;
      let width = (entry.target as HTMLElement).offsetWidth;

      const style = window.getComputedStyle(parentNode) || {};
      width -= parseInt(style.paddingLeft, 10) || 0;
      width -= parseInt(style.paddingRight, 10) || 0;
      height -= parseInt(style.paddingTop, 10) || 0;
      height -= parseInt(style.paddingBottom, 10) || 0;

      if (
        (!ignoreHeight && dimensions?.height !== height) ||
        (!ignoreWidth && dimensions?.width !== width)
      ) {
        setDimensions({ height, width });
      }
    }
  });

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      const parentElement = element?.parentElement ?? null;
      setParentRef(parentElement);
    },
    [setParentRef]
  );

  return [setRef, dimensions];
}
